import { normalizeAlias, type PublicLocation } from '@irace/shared';
import { chooseLocationMode } from './validation.ts';

/**
 * What the URL says about where the participant is standing.
 *
 * Kept apart from the page so the rule can be read and tested without a DOM,
 * and because the two URL shapes below are easy to conflate in a component
 * where they would sit three props apart.
 */
export type LocationIntent =
  | { kind: 'none' }
  | { kind: 'locked'; location: PublicLocation }
  | { kind: 'preselected'; location: PublicLocation }
  | { kind: 'unknown' };

/**
 * Match a code from the URL against the venues this event actually serves.
 *
 * An exact match wins first, then the `normalizeAlias` fold the location
 * search already uses, so a QR printed as `/e/.../dn` finds `ĐN` while an
 * event that has both `DN` and `ĐN` still sends each URL to its own venue.
 * The placeholder row is excluded from both passes: it exists only to satisfy
 * the foreign key on `checkins`, and no printed URL should name it.
 */
function findByCode(
  locations: readonly PublicLocation[],
  code: string,
): PublicLocation | undefined {
  const selectable = locations.filter((location) => !location.is_placeholder);

  const exact = code.trim();
  if (exact.length === 0) return undefined;
  const exactMatch = selectable.find((location) => location.code.trim() === exact);
  if (exactMatch) return exactMatch;

  const wanted = normalizeAlias(code);
  if (wanted.length === 0) return undefined;
  return selectable.find((location) => normalizeAlias(location.code) === wanted);
}

/**
 * Decide from the URL whether a location is locked, merely preselected, or
 * wrong.
 *
 * `/e/:slug/:code` is a QR taped to one venue's door, so it locks and the
 * participant is not asked. `?loc=` came first and §M03 promised the picker
 * stayed open; codes in that form are already printed, so its behaviour is
 * left exactly as it was. A code neither list knows is a wrong QR — the form
 * says so instead of silently dropping the participant into a picker they
 * were told they would not see.
 */
export function resolveLocationIntent(
  locations: readonly PublicLocation[],
  locationCode: string | null,
  search: string,
): LocationIntent {
  if (locationCode) {
    const location = findByCode(locations, locationCode);
    return location ? { kind: 'locked', location } : { kind: 'unknown' };
  }

  const queryCode = new URLSearchParams(search).get('loc');
  if (!queryCode || queryCode.trim().length === 0) return { kind: 'none' };

  const location = findByCode(locations, queryCode);
  return location ? { kind: 'preselected', location } : { kind: 'unknown' };
}

/**
 * What the participant sees where the location goes.
 *
 * `hidden` and `fixed` come from the event itself: no real venue, or exactly
 * one. `locked` is the new URL shape doing its job. `pick` is the picker,
 * carrying whether the URL named a venue that does not exist so the form can
 * say so instead of leaving an unexplained empty field.
 */
export type LocationControl =
  | { kind: 'hidden' }
  | { kind: 'fixed'; location: PublicLocation }
  | { kind: 'locked'; location: PublicLocation }
  | { kind: 'pick'; notFound: boolean };

/**
 * Combine the event's venue count with what the URL asked for.
 *
 * The venue count wins first: an event with one venue already shows that
 * venue and cannot offer a picker, so a wrong code in the URL changes nothing
 * the participant could act on and the notice would only be noise. `unlocked`
 * is the participant having tapped through to the picker, which is a one-way
 * door for the rest of the visit.
 */
export function chooseLocationControl(
  locations: readonly PublicLocation[],
  intent: LocationIntent,
  unlocked: boolean,
): LocationControl {
  const mode = chooseLocationMode(locations);
  if (mode.mode === 'hidden') return { kind: 'hidden' };
  if (mode.mode === 'fixed' && mode.selected) return { kind: 'fixed', location: mode.selected };

  if (intent.kind === 'locked' && !unlocked) return { kind: 'locked', location: intent.location };
  return { kind: 'pick', notFound: intent.kind === 'unknown' };
}

/**
 * Which location the form starts on.
 *
 * For an event with no real venue this is the placeholder row, which never
 * appears on screen but is always what gets submitted: `checkins.location_id`
 * is NOT NULL and every report keys on it.
 */
export function initialLocation(
  locations: readonly PublicLocation[],
  intent: LocationIntent,
): PublicLocation | null {
  if (intent.kind === 'locked' || intent.kind === 'preselected') return intent.location;
  return chooseLocationMode(locations).selected ?? null;
}
